import React, { useEffect, useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'

type VariantCardProps = {
  index: number
  isGenerating: boolean
  resultImageUrl: string | null
  isSelected: boolean
  onSelect: () => void
  onReprompt: (prompt: string) => void
  onRetry: () => void
}

function VariantCard({
  index,
  isGenerating,
  resultImageUrl,
  isSelected,
  onSelect,
  onReprompt,
  onRetry,
}: VariantCardProps) {
  const [refining, setRefining] = useState(false)
  const [refinePrompt, setRefinePrompt] = useState('')
  const [timedOut, setTimedOut] = useState(false)

  const isPending = isGenerating
  const isComplete = !isGenerating && !!resultImageUrl
  const isFailed = !isGenerating && !resultImageUrl

  const isPendingRef = useRef(isPending)
  isPendingRef.current = isPending

  useEffect(() => {
    if (!isPending) {
      setTimedOut(false)
      return
    }
    const timer = setTimeout(() => {
      if (isPendingRef.current) setTimedOut(true)
    }, 60_000)
    return () => clearTimeout(timer)
  }, [isPending])

  const handleRepromptSubmit = () => {
    if (!refinePrompt.trim()) return
    onReprompt(refinePrompt.trim())
    setRefinePrompt('')
    setRefining(false)
  }

  return (
    <div
      className={`flex flex-col rounded-2xl border-2 overflow-hidden transition-all ${
        isSelected
          ? 'border-purple-600 shadow-lg shadow-purple-100'
          : isComplete
          ? 'border-gray-200 hover:border-purple-300 cursor-pointer'
          : 'border-gray-200'
      }`}
      onClick={() => {
        if (isComplete && !refining) onSelect()
      }}
    >
      {/* Image area */}
      <div className="relative bg-gray-50">
        {isPending && (
          <div className="flex flex-col items-center justify-center gap-3 py-24">
            {timedOut ? (
              <>
                <i className="fa fa-clock text-gray-400 text-3xl" />
                <p className="text-sm text-gray-500 text-center px-4">
                  This variant is taking longer than expected
                </p>
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation()
                    onRetry()
                  }}
                  className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors"
                >
                  <i className="fa fa-rotate-right mr-2" />
                  Retry
                </button>
              </>
            ) : (
              <>
                <CircularProgress color="primary" size={40} />
                <p className="text-sm text-gray-500">Generating variant {index + 1}...</p>
              </>
            )}
          </div>
        )}

        {isFailed && (
          <div className="flex flex-col items-center justify-center gap-3 py-24 bg-red-50">
            <i className="fa fa-circle-exclamation text-red-400 text-3xl" />
            <p className="text-sm text-red-600 text-center px-4">Generation failed</p>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                onRetry()
              }}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              <i className="fa fa-rotate-right mr-2" />
              Retry
            </button>
          </div>
        )}

        {isComplete && (
          <img src={resultImageUrl!} alt={`Variant ${index + 1}`} className="w-full h-auto block" />
        )}

        {/* Selected checkmark */}
        {isSelected && (
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center shadow-md">
            <i className="fa fa-check text-white text-sm" />
          </div>
        )}

        {/* Variant label */}
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-semibold text-gray-600 shadow-sm">
          Variant {index + 1}
        </div>
      </div>

      {/* Card footer */}
      {isComplete && (
        <div className="flex-shrink-0 border-t border-gray-100 bg-white">
          {refining ? (
            <div className="p-3 flex flex-col gap-2" onClick={e => e.stopPropagation()}>
              <textarea
                autoFocus
                value={refinePrompt}
                onChange={e => setRefinePrompt(e.target.value)}
                rows={3}
                placeholder="Describe the changes you want for this variant..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setRefining(false)
                    setRefinePrompt('')
                  }}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRepromptSubmit}
                  disabled={!refinePrompt.trim()}
                  className="flex-1 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Regenerate
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col" onClick={e => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setRefining(true)}
                className="w-full px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-purple-700 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
              >
                <i className="fa fa-wand-sparkles text-xs" />
                Refine this variant
              </button>
              <a
                href={resultImageUrl!}
                download
                className="w-full px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-purple-700 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2 border-t border-gray-100"
              >
                <i className="fa fa-download text-xs" />
                Download
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

type Props = {
  variants: { jobID: string | null; resultImageUrl: string | null; isGenerating: boolean }[]
  selectedIndex: number | null
  onSelect: (index: number) => void
  onReprompt: (index: number, prompt: string) => void
  onRetry: (index: number) => void
  onRetryAll: () => void
  onBack: () => void
  onNext: () => void
}

export function VariantsStep({
  variants,
  selectedIndex,
  onSelect,
  onReprompt,
  onRetry,
  onRetryAll,
  onBack,
  onNext,
}: Props) {
  const selectedVariant = selectedIndex !== null ? variants[selectedIndex] : undefined
  const canProceed = selectedIndex !== null && !!selectedVariant?.resultImageUrl
  const isAnyGenerating = variants.some(v => v.isGenerating)

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto p-8">
        <div className="w-full">
          <p className="text-sm text-gray-500 mb-6">
            Select the variant you want to use. You can refine any variant before choosing.
          </p>
          <div className="flex flex-col gap-8">
            {[variants.slice(0, 3), variants.slice(3, 6)].map((row, rowIndex) => (
              <div key={rowIndex} className="grid grid-cols-3 gap-6">
                {row.map((variant, colIndex) => {
                  const index = rowIndex * 3 + colIndex
                  return (
                    <VariantCard
                      key={index}
                      index={index}
                      isGenerating={variant.isGenerating}
                      resultImageUrl={variant.resultImageUrl}
                      isSelected={selectedIndex === index}
                      onSelect={() => onSelect(index)}
                      onReprompt={prompt => onReprompt(index, prompt)}
                      onRetry={() => onRetry(index)}
                    />
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 flex items-center justify-between shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <i className="fa fa-arrow-left" />
          Back
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onRetryAll}
            disabled={isAnyGenerating}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="fa fa-rotate-right" />
            Re-Generate All
          </button>

          <button
            type="button"
            onClick={onNext}
            disabled={!canProceed}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-purple-600 text-white font-semibold text-sm hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
          >
            Use this variant
            <i className="fa fa-arrow-right" />
          </button>
        </div>
      </div>
    </div>
  )
}
